/* eslint-disable max-lines-per-function */
import React from 'react'
import { CrossRefPaper } from '@cpsy-rmap-project/web-core/crossref'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Divider from '@material-ui/core/Divider'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import _ from 'lodash'
import { usePrevious } from 'react-use'
import styled from 'styled-components'
import { customTheme } from '../../../libraries/MaterialUI'
import { SupplementBox } from './common'

const Root = styled(Box)`
  margin: ${customTheme.spacing(0, 1)};
`

const CustomDivider = styled(Divider)`
  margin: ${customTheme.spacing(2, 0)} !important;
`

interface InputDetailsViewProps {
  type: 'submit' | 'edit' | 'editRequest'
  paper: CrossRefPaper
  handleBack: () => void
  handleNext: () => void
  handleDetailsChange: (update: Partial<CrossRefPaper>) => void
  handleIsReadySubmit: (isReadySubmit: boolean) => void
}

const textFieldCommonProps = {
  variant: 'outlined' as const,
  fullWidth: true,
  InputLabelProps: {
    shrink: true
  },
  margin: 'normal' as const
}

const Component: React.FC<InputDetailsViewProps> = ({
  type,
  paper,
  handleBack,
  handleNext,
  handleDetailsChange,
  handleIsReadySubmit
}) => {
  // Paper State
  const [tmpPaper, setTmpPaper] = React.useState<CrossRefPaper>(paper)
  const ref = React.useRef()

  const textFieldMultiline = {
    multiline: true,
    rows: '5',
    rowsMax: '18'
  }

  const previousTmpPaper = usePrevious(tmpPaper)
  React.useEffect(() => {
    if (previousTmpPaper !== undefined && !_.isEqual(previousTmpPaper, tmpPaper)) {
      handleDetailsChange(tmpPaper)
    }
  }, [handleDetailsChange, paper, previousTmpPaper, tmpPaper])

  const handleSubmit = React.useCallback(() => {
    handleIsReadySubmit(true)
    setTimeout(() => {
      handleNext() // 時間差でready系のstateが発動してからnextに移動する
    }, 100)
  }, [handleIsReadySubmit, handleNext])

  return (
    <Root>
      <Typography variant="h4">Required fields</Typography>
      <TextField
        required
        {...textFieldCommonProps}
        placeholder="ex. Addiction as a Computational Process Gone Awry"
        label="Title"
        name="title"
        value={tmpPaper.title}
        onChange={e => {
          setTmpPaper({ ...tmpPaper, title: e.target.value })
        }}
        innerRef={ref}
        type="text"
      />
      <TextField
        required
        {...textFieldCommonProps}
        placeholder="ex. A. David Redish, ..."
        label="Authors"
        name="authors"
        value={tmpPaper.authors}
        onChange={e => {
          setTmpPaper({ ...tmpPaper, authors: e.target.value })
        }}
        type="text"
      />
      <TextField
        required
        {...textFieldCommonProps}
        placeholder="ex. 2004"
        label="Year"
        name="year"
        value={tmpPaper.year ?? ''}
        onChange={e => {
          const num = _.isEmpty(e.target.value) ? undefined : _.toNumber(e.target.value)
          setTmpPaper({ ...tmpPaper, year: _.isNaN(num) ? undefined : num })
        }}
        type="text"
      />
      <TextField
        required
        {...textFieldCommonProps}
        placeholder="ex. https://science.sciencemag.org/content/306/5703/1944"
        label="URL"
        name="url"
        value={tmpPaper.url}
        onChange={e => {
          setTmpPaper({ ...tmpPaper, url: e.target.value })
        }}
        type="text"
      />
      <Typography variant="body2" align="right">
        <a href={tmpPaper.url} target="_blank" rel="noopener noreferrer">
          Go to paper (Open in new tab) <FontAwesomeIcon icon={faExternalLinkAlt} />
        </a>
      </Typography>
      <CustomDivider />
      <Typography variant="h4">Optional Fields</Typography>
      <TextField
        {...textFieldCommonProps}
        placeholder="ex. Science"
        label="Journal"
        name="journal"
        value={tmpPaper.journal}
        onChange={e => {
          setTmpPaper({ ...tmpPaper, journal: e.target.value })
        }}
        type="text"
      />
      <TextField
        {...textFieldCommonProps}
        {...textFieldMultiline}
        placeholder="ex. Addictive drugs have been hypothesized to access the same neurophysiological mechanisms as natural learning systems. These natural learning systems..."
        label="Abstract"
        name="abstract"
        value={tmpPaper.abstract}
        onChange={e => {
          setTmpPaper({ ...tmpPaper, abstract: e.target.value })
        }}
        type="text"
      />
      <TextField
        {...textFieldCommonProps}
        {...textFieldMultiline}
        placeholder="Please describe the assumptions which this research built upon.
-
ex. Drug intake produces a non-compensable dopamine (positive prediction error) increase in temporal difference reinforcement learning model."
        label="Hypothesis"
        name="hypothesis"
        value={tmpPaper.hypothesis}
        onChange={e => {
          setTmpPaper({ ...tmpPaper, hypothesis: e.target.value })
        }}
        type="text"
      />
      <TextField
        {...textFieldCommonProps}
        {...textFieldMultiline}
        placeholder="Please describe the predictions that this research made.
-
ex:  The presented model predicts that drugs of abuse will show increasing inelasticity with use."
        label="Implication"
        name="prediction"
        value={tmpPaper.prediction}
        onChange={e => {
          setTmpPaper({ ...tmpPaper, prediction: e.target.value })
        }}
        type="text"
      />
      <TextField
        {...textFieldCommonProps}
        {...textFieldMultiline}
        placeholder="Any relevant experimental or modelling studies
-
ex: Later computational model modified this model
Dezfouli et al., 2009 https://www.ncbi.nlm.nih.gov/pubmed/19635010"
        label="Related papers"
        name="relevantPapers"
        value={tmpPaper.relevantPapers}
        onChange={e => {
          setTmpPaper({ ...tmpPaper, relevantPapers: e.target.value })
        }}
        type="text"
      />
      <TextField
        {...textFieldCommonProps}
        {...textFieldMultiline}
        placeholder="Freewriting space to describe this research. Please use this space for any additional detail about this research. If code for this research is available, please paste a link to it here."
        label="Comments"
        name="comment"
        value={tmpPaper.comment}
        onChange={e => {
          setTmpPaper({ ...tmpPaper, comment: e.target.value })
        }}
        type="text"
      />
      <SupplementBox>
        Make sure the papers details are correct. Always click “Back” on this interface and DO NOT use the back button
        on the browser when editing tags. Registered papers can be edited any time in the ”Review/edit your submitted
        paper” section.
      </SupplementBox>
      <CustomDivider />
      <Box style={{ textAlign: 'center', marginTop: '20px' }}>
        <Box style={{ margin: '10px' }}>
          <Button variant="outlined" color="primary" size="medium" onClick={handleSubmit}>
            {type === 'submit' && 'Submit'}
            {type === 'edit' && 'Edit'}
            {type === 'editRequest' && 'Edit Request'}
          </Button>
        </Box>
        <Box style={{ margin: '10px' }}>
          <Button variant="text" color="default" size="small" onClick={handleBack}>
            Back
          </Button>
        </Box>
      </Box>
    </Root>
  )
}

export default Component
